import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import portalClient from './portalClient';
import PortalLayout from './PortalLayout';
import { Button, Card, Skeleton, useToast } from '../design-system';
import './Portal.css';

const fmtWhen = (d) =>
  d ? new Date(d).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }) : '';

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const STATUS_TEXT = { open: 'Open', in_progress: 'In progress', fulfilled: 'Done', closed: 'Closed' };

// The two-way conversation. This is the heart of the portal: the firm asks, the
// client replies and attaches files, both sides see one thread.
//
// The client only ever sees comments the firm marked shareable — the server
// filters them out, so internal notes cannot reach this component at all.
function PortalRequestDetail() {
  const { id } = useParams();
  const toast = useToast();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const threadEndRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await portalClient.get(`/api/portal/requests/${id}`);
      setRequest(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not open this request');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Keep the newest message in view — a long thread otherwise opens at the top,
  // showing the oldest message, which is never the one you came back for.
  // Depends on the message COUNT, not the request: re-scrolling because an
  // unrelated field changed would yank the view while someone is reading.
  const messageCount = request?.comments?.length ?? 0;
  useEffect(() => {
    if (messageCount) threadEndRef.current?.scrollIntoView({ block: 'nearest' });
  }, [messageCount]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res = await portalClient.post(`/api/portal/requests/${id}/comments`, { text: reply });
      setRequest((prev) => ({ ...prev, comments: res.data }));
      setReply('');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not send your message');
    } finally {
      setSending(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.danger('That file is larger than the 25MB limit');
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      // No Content-Type header: axios must set it so the multipart boundary
      // is included.
      await portalClient.post(`/api/portal/requests/${id}/documents`, body, { timeout: 120000 });
      toast.success('Uploaded — your accountant can see it now');
      await load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const openDoc = async (doc) => {
    try {
      const res = await portalClient.get(`/api/portal/documents/${doc.id}/url`);
      if (res.data?.url) window.open(res.data.url, '_blank', 'noreferrer');
    } catch {
      toast.danger('Could not open that file');
    }
  };

  if (loading) {
    return <PortalLayout title="Request"><Card><Skeleton.Text lines={6} /></Card></PortalLayout>;
  }
  if (!request) {
    return (
      <PortalLayout title="Request">
        <Card padding="lg">
          <p>We couldn&apos;t find that request.</p>
          <Link to="/portal/requests" className="portal-link">Back to requests</Link>
        </Card>
      </PortalLayout>
    );
  }

  const closed = request.status === 'closed';

  return (
    <PortalLayout>
      <div className="portal-thread-head">
        <Link to="/portal/requests" className="portal-link">← All requests</Link>
        <span className={`portal-status portal-status-${request.status}`}>
          {STATUS_TEXT[request.status] || request.status}
        </span>
      </div>

      <Card padding="md">
        <h1 className="portal-thread-title">{request.title}</h1>
        {request.description && <p className="portal-thread-desc">{request.description}</p>}
        <p className="portal-muted portal-thread-meta">
          {request.raisedByClient ? 'You asked this' : 'Requested by your accountant'}
          {request.dueDate && ` · needed by ${new Date(request.dueDate).toLocaleDateString()}`}
        </p>
      </Card>

      {request.documents?.length > 0 && (
        <Card padding="md">
          <div className="portal-card-head"><span className="portal-card-title">Files on this request</span></div>
          <ul className="portal-file-list">
            {request.documents.map((d) => (
              <li key={d.id}>
                <button type="button" className="portal-file-row" onClick={() => openDoc(d)}>
                  <span>📄 {d.title}</span>
                  <span className="portal-muted">
                    {d.fromClient ? 'You' : 'Your accountant'}
                    {d.sizeBytes ? ` · ${fmtSize(d.sizeBytes)}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card padding="md">
        <div className="portal-card-head"><span className="portal-card-title">Messages</span></div>

        {request.comments.length === 0 ? (
          <p className="portal-muted">No messages yet. Say hello, or attach what was asked for.</p>
        ) : (
          <div className="portal-thread">
            {request.comments.map((c) => (
              <div key={c.id} className={`portal-msg${c.fromClient ? ' is-mine' : ''}`}>
                <div className="portal-msg-bubble">
                  <span className="portal-msg-author">{c.fromClient ? 'You' : 'Your accountant'}</span>
                  <p className="portal-msg-text">{c.text}</p>
                  <span className="portal-msg-when">{fmtWhen(c.createdAt)}</span>
                </div>
              </div>
            ))}
            <div ref={threadEndRef} />
          </div>
        )}

        {closed ? (
          <p className="portal-muted portal-closed-note">
            This request is closed. Start a new one from the Requests page if you need anything else.
          </p>
        ) : (
          <form className="portal-reply" onSubmit={send}>
            <textarea
              className="ds-input portal-reply-input"
              rows="3"
              placeholder="Write a message…"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
            />
            <div className="portal-reply-actions">
              <input
                ref={fileRef}
                type="file"
                className="portal-file-input"
                onChange={(e) => upload(e.target.files?.[0])}
                disabled={uploading}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => fileRef.current?.click()}
                loading={uploading}
              >
                📎 Attach a file
              </Button>
              <Button type="submit" variant="primary" loading={sending} disabled={!reply.trim()}>
                Send
              </Button>
            </div>
          </form>
        )}
      </Card>
    </PortalLayout>
  );
}

export default PortalRequestDetail;
